import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { QUERY_KEYS } from "../utils/project-constants";

interface UseUploadPlanMutationProps {
  projectId: string;
}

interface UploadPlanResponse {
  success?: boolean;
  message?: string;
  data?: {
    id?: string;
    name?: string;
    fileUrl?: string;
    [key: string]: any;
  };
  limits?: {
    maxPdfPlans?: number;
    currentCount?: number;
  };
}

export function useUploadPlanMutation({ projectId }: UseUploadPlanMutationProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation(["pages.projectDetails"]);

  const uploadPlanMutation = useMutation({
    mutationFn: async (file: File): Promise<UploadPlanResponse> => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("projectId", projectId);
      formData.append("name", file.name.replace(/\.[^/.]+$/, ""));

      const response = await apiRequest("/api/plans", {
        method: "POST",
        body: formData,
      });

      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.projectPlans(projectId),
        refetchType: "active",
      });

      toast({
        title: t("toasts.success"),
        description: t("toasts.planUploaded"),
      });
    },
    onError: (error: any) => {
      console.error("Upload plan error:", error);

      const code = error?.code || error?.data?.error;
      const message = error?.message || "";
      if (code === "PDF_PLAN_LIMIT_REACHED" || message.includes("PDF-Plan-Limit")) {
        toast({
          title: t("toasts.pdfPlanLimitReachedTitle"),
          description: t("toasts.pdfPlanLimitReachedMessage"),
          variant: "destructive",
        });
        return;
      }

      toast({
        title: t("toasts.error"),
        description: t("toasts.uploadPlanError"),
        variant: "destructive",
      });
    },
  });

  return {
    uploadPlan: uploadPlanMutation.mutateAsync,
    isUploading: uploadPlanMutation.isPending,
  };
}
